import ReportTable from "../ReportTable.component";
import { useEffect, useState, useMemo, useRef } from "react";
import ReportModal from "../ReportModal.component";
import ReportService from "../../../services/reports/Report.service";
import BaseReport from "../BaseReport.component";
import { BuyerPerformanceParsedData, BuyerPerformanceRawData } from "../../../interfaces/reports/BuyerPerformanceReport.interface";
import { MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import TipContext from "../../../providers/GlobalTip";
import EpochMonthService from "../../../services/EpochMonth.service";
import MonthSelector from "../../../tailwindComponents/ui/dates/month-selector.component";

interface BuyerPerformanceReportProps {
    clientId: string | number;
    epochMonth: number | null;
}

const HIERARCHY_OPTIONS = {
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location by Class by Child": ["locationCode", "parentClientClassCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Child": ["parentClientClassCode", "classCode"],
    "Class by Child by Location": ["parentClientClassCode", "classCode", "locationCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
    "Category by Class by Child": ["category", "parentClientClassCode", "classCode"],
}
 
// Moved data processing to a separate function
const processRawData = (rawData: BuyerPerformanceRawData[]): BuyerPerformanceParsedData[] => {
    if (!rawData || rawData.length === 0) return [];

    // Parse the raw data into controlled and formattable types
    return rawData.map((item) => {
        return {
            locationCode: item.locationCode,
            locationName: item.locationName,
            classCode: item.classCode,
            className: item.className,
            parentClientClassCode: item.parentClientClassCode ?? null,
            category: item.category ? item.category == "" ? "N/A" : item.category : "N/A",
            categorySortOrder: item.categorySortOrder ?? null,

            sales: new MoneyDataItem(item.sales),
            inflowCost: new MoneyDataItem(item.inflowCost),
            inflowRetail: new MoneyDataItem(item.inflowRetail),
            originalSalesPlan: new MoneyDataItem(item.originalSalesPlan),
            revisedSalesPlan: new MoneyDataItem(item.revisedSalesPlan), 
            originalInflowCostPlan: new MoneyDataItem(item.originalInflowCostPlan),
            revisedInflowCostPlan: new MoneyDataItem(item.revisedInflowCostPlan),
            originalInflowRetailPlan: new MoneyDataItem(item.originalInflowRetailPlan),
            revisedInflowRetailPlan: new MoneyDataItem(item.revisedInflowRetailPlan),
        }
    });
};

const BuyerPerformanceReport = ({ clientId, epochMonth }: BuyerPerformanceReportProps) => {
    const globalTip = TipContext.useMessage();
    const today = EpochMonthService.getTodayEpochMonth();
    const dataRef = useRef<BuyerPerformanceRawData[]>([]);
    const parentDataRef = useRef<BuyerPerformanceRawData[]>([]);
    const [parsedData, setParsedData] = useState<BuyerPerformanceParsedData[]>([]);
    const [parentParsedData, setParentParsedData] = useState<BuyerPerformanceParsedData[]>([]);
    const [sortedData, setSortedData] = useState<BuyerPerformanceParsedData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hierarchy, setHierarchy] = useState<string[] | null>(["locationCode", "classCode"]);
    const [startEpochMonth, setStartEpochMonth] = useState<number>(epochMonth ? epochMonth - 4 : today - 4);
    const [endEpochMonth, setEndEpochMonth] = useState<number>(epochMonth ?? today);

    // Filters
    const [locationFilter, setLocationFilter] = useState<string[]>([]);
    const [classFilter, setClassFilter] = useState<string[]>([]);
    const [categoryFilter, setCategoryFilter] = useState<string[]>([]);

    let hierarchyColumns = [];
    for (let i = 0; i < (hierarchy?.length ?? 0); i++) {
        hierarchyColumns.push({
            name: "",
            sortable: false,
            customClassName: i === 0
                ? "px-1 py-2 bg-gray-500 text-white"
                : "px-1 py-2 bg-purple-100 border-purple-700 text-purple-800"
        });
    }

    const columns = [
        ...hierarchyColumns,
        { name: "Sales Plan Original", sortable: false },
        { name: "Sales Plan Revised", sortable: false },
        { name: "Sales", sortable: false },
        { name: "Inflow Plan Original", sortable: false },
        { name: "Inflow Plan Revised", sortable: false },
        { name: "Inflow Actual", sortable: false },
        { name: "Sales Variance $", sortable: false },
        { name: "Sales Variance %", sortable: false },
        { name: "Inflow Variance $", sortable: false },
        { name: "Inflow Variance %", sortable: false },
        { name: "Cash Margin Forecast", sortable: false },
        { name: "Cash Margin Actual", sortable: false },
        { name: "Cash Margin Variance", sortable: false },
    ];

    useEffect(() => {
        setStartEpochMonth(endEpochMonth - 4);
    }, [endEpochMonth]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            ReportService.getBuyerPerformanceReportData({ clientId, startEpochMonth, endEpochMonth }).then((response) => {
                if (response) {
                    // Store raw data in refs
                    dataRef.current = response.data ?? [];
                    parentDataRef.current = response.parentLevelData ?? [];

                    // Process both child and parent data
                    const processed = processRawData(response.data ?? []);
                    const processedParent = processRawData(response.parentLevelData ?? []);
                    setParsedData(processed);
                    setParentParsedData(processedParent);

                    // Default: use child data
                    let sorted = ReportService.groupByHierarchy(processed, hierarchy ?? ["locationCode", "classCode"]);
                    setSortedData(sorted);
                } else {
                    dataRef.current = [];
                    parentDataRef.current = [];
                    setParsedData([]);
                    setParentParsedData([]);
                    setSortedData([]);
                }
                setLoading(false);
            }).catch((error) => {
                if (globalTip)
                    globalTip.show("Failed to fetch data");
                setLoading(false);
            });
        };
        fetchData();
    }, [clientId, startEpochMonth, endEpochMonth]);

    useEffect(() => {
        // Use parent or child data based on hierarchy
        const useParent = hierarchy?.includes("parentClientClassCode");
        const sourceData = useParent ? parentParsedData : parsedData;

        if (sourceData.length > 0) {
            let newSortedData = ReportService.sortAndFilterData(
                sourceData,
                hierarchy ?? ["locationCode", "classCode"],
                locationFilter,
                classFilter,
                categoryFilter
            );
            setSortedData(newSortedData);
        }
    }, [hierarchy, parsedData, parentParsedData, locationFilter, classFilter, categoryFilter]);

    const tableData = useMemo(() => {

        return sortedData.map((item: BuyerPerformanceParsedData, index) => {
            const labelRows = ReportService.hierarchyItemToVerticalDisplayNames(item, hierarchy ?? ["locationCode", "classCode"]);

            const salesOriginalPlan = Number(item.originalSalesPlan.value);
            const salesRevisedPlan = Number(item.revisedSalesPlan.value);
            const salesActual = Number(item.sales.value);
            const inflowRetailOriginalPlan = Number(item.originalInflowRetailPlan.value);
            const inflowRetailRevisedPlan = Number(item.revisedInflowRetailPlan.value);
            const inflowCostOriginalPlan = Number(item.originalInflowCostPlan.value);
            const inflowCostRevisedPlan = Number(item.revisedInflowCostPlan.value);
            const inflowRetailActual = Number(item.inflowRetail.value);
            const inflowCostActual = Number(item.inflowCost.value);

            const cashMarginForecast = ReportService.calculateCashMargin({
                sales: salesOriginalPlan,
                inflowCost: inflowCostOriginalPlan,
                format: false
            });
            const cashMarginActual = ReportService.calculateCashMargin({
                sales: salesActual,
                inflowCost: inflowCostActual,
                format: false
            });

            return [
                ...labelRows,
                item.originalSalesPlan.formatted,
                item.revisedSalesPlan.formatted,
                item.sales.formatted,
                item.originalInflowRetailPlan.formatted,
                item.revisedInflowRetailPlan.formatted,
                item.inflowRetail.formatted,
                ReportService.formatCurrency(salesActual - salesRevisedPlan),
                ReportService.formatPercent((salesRevisedPlan === 0 ? 0 : (salesActual - salesRevisedPlan) / Math.abs(salesRevisedPlan)) * 100),
                ReportService.formatCurrency(inflowRetailActual - inflowRetailRevisedPlan),
                ReportService.formatPercent((inflowRetailRevisedPlan === 0 ? 0 : (inflowRetailActual - inflowRetailRevisedPlan) / Math.abs(inflowRetailRevisedPlan)) * 100),
                ReportService.formatCurrency(cashMarginForecast),
                ReportService.formatCurrency(cashMarginActual),
                ReportService.formatCurrency(Number(cashMarginActual) - Number(cashMarginForecast)),
            ];
        });
    }, [sortedData, hierarchy]);

    const rowClassName = (rowIndex: number) => {
        if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
        return ReportService.getRowClassName(tableData[rowIndex], hierarchy ?? ["locationCode", "classCode"]);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-center">
            <h2 className="text-2xl font-bold mb-4">{`Buyer Performance | ${EpochMonthService.epochMonthToMMMYY(endEpochMonth)}: Original ${EpochMonthService.epochMonthToMMMYY(startEpochMonth)} + ${endEpochMonth - startEpochMonth - 1} Months`}</h2>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <div className="w-full h-full">
                    {/* BaseReport for PDF/CSV generation */}
                    <BaseReport
                        title="Buyer Performance Report"
                        tableColumns={columns.map((col) => col.name)}
                        tableData={tableData}
                        fileName={`Buyer_Performance_${new Date().toLocaleDateString()}`}
                        setHierarchy={setHierarchy}
                        hierarchy={hierarchy}
                        hierarchyOptions={HIERARCHY_OPTIONS}

                        parsedData={parsedData}
                        filteredLocationCodes={locationFilter}
                        filteredClassCodes={classFilter}
                        setFilteredLocationCodes={setLocationFilter}
                        setFilteredClassCodes={setClassFilter}
                        filteredCategories={categoryFilter}
                        setFilteredCategories={setCategoryFilter}

                        onApplyFilters={() => {
                            // Reparse
                            let parsedData = processRawData(dataRef.current);
                            setParsedData(parsedData);
                            let parsedParentData = processRawData(parentDataRef.current);
                            setParentParsedData(parsedParentData);
                        }}
                    />
                    <div className="flex flex-row flex-wrap items-center space-x-4 mb-4 ml-8">
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">Start Period</span>
                            <MonthSelector epochMonth={startEpochMonth} setEpochMonth={setStartEpochMonth} minMonth={endEpochMonth - 14} maxMonth={endEpochMonth - 2} />
                        </div>
                        <div className="flex flex-col items-start">
                            <span className="text-xs font-semibold text-slate-600">End Period</span>
                            <MonthSelector epochMonth={endEpochMonth} setEpochMonth={setEndEpochMonth} minMonth={today - 11} maxMonth={today} />
                        </div>
                    </div>
                    
                    {/* Display table with export buttons */}
                    <ReportTable
                        columns={columns}
                        data={tableData}
                        rowClassName={rowClassName}
                        defaultSortColumn="Sales"
                        defaultSortDirection="desc"
                        headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
                        toPdf={() => (window as any).BaseReportExport && (window as any).BaseReportExport('pdf')}
                        toCsv={() => (window as any).BaseReportExport && (window as any).BaseReportExport('csv')}
                        stickyHeader={true}
                        hierarchyDepth={hierarchy ? hierarchy.length : 0}
                    />
                </div>
            )}
        </div>
    );
};

interface BuyerPerformanceReportModalProps {
    clientId: string | number;
    epochMonth: number | null;
    onClose: () => void;
}

const BuyerPerformanceReportModal = ({ clientId, epochMonth, onClose }: BuyerPerformanceReportModalProps) => {
    return (
        <ReportModal
            onClose={onClose}
        >
            <BuyerPerformanceReport clientId={clientId} epochMonth={epochMonth} />
        </ReportModal>
    );
};

export {
    BuyerPerformanceReport,
    BuyerPerformanceReportModal
}